/**
 * The 8-step job application (partials/job-application-form.php).
 *
 * One form, one fieldset per step. Next checks only the current step's
 * enabled fields with the browser's own constraint validation; fields inside
 * a hidden [data-show-if] block are disabled, so they neither block a step
 * nor get submitted. Repeatable rows (schools, employers, references) are
 * cloned from each list's <template>. The last step posts the whole form to
 * pcm_send_zoho_application, refreshing the nonce once on a 403.
 */
(function () {
  'use strict';

  const form = document.querySelector('[data-job-application]');
  if (!form) {
    return;
  }

  const text = window.pcmJobApplication || {};
  const steps = Array.from(form.querySelectorAll('[data-step]'));
  const navButtons = Array.from(form.querySelectorAll('[data-step-nav]'));
  const count = form.querySelector('[data-step-count]');
  const bar = form.querySelector('[data-step-bar]');
  const prev = form.querySelector('[data-step-prev]');
  const next = form.querySelector('[data-step-next]');
  const nextLabel = form.querySelector('[data-next-label]');
  const status = form.querySelector('[data-app-status]');
  const conditionals = Array.from(form.querySelectorAll('[data-show-if]'));
  const name = form.elements.name;
  const signature = form.elements.digital_signature;
  const ajaxUrl = form.getAttribute('action'); // the "action" input shadows form.action

  let current = 0;
  let reached = 0;
  let dirty = false;
  let sending = false;

  function show(index) {
    current = index;
    reached = Math.max(reached, index);
    steps.forEach((step, i) => {
      step.hidden = i !== index;
    });
    navButtons.forEach((button, i) => {
      button.disabled = i > reached;
      if (i === index) {
        button.setAttribute('aria-current', 'step');
      } else {
        button.removeAttribute('aria-current');
      }
    });
    count.textContent = index + 1;
    bar.style.width = ((index + 1) / steps.length) * 100 + '%';
    prev.hidden = index === 0;
    nextLabel.textContent = index === steps.length - 1 ? text.submit : text.next;
    setStatus('');

    if (form.getBoundingClientRect().top < 0) {
      form.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    steps[index].querySelector('legend').focus({ preventScroll: true });
  }

  function stepIsValid(step) {
    step.classList.add('was-validated');
    const invalid = Array.from(step.querySelectorAll('input, select, textarea')).find((field) => !field.checkValidity());
    if (invalid) {
      invalid.reportValidity();
      return false;
    }
    return true;
  }

  function setStatus(message, type) {
    status.textContent = message;
    status.classList.toggle('is-error', type === 'error');
    status.classList.toggle('is-success', type === 'success');
  }

  // [data-show-if="field=value"] blocks, e.g. the referral name or the
  // conviction details.
  function syncConditionals() {
    conditionals.forEach((block) => {
      const [field, value] = block.dataset.showIf.split('=');
      const control = form.elements[field];
      const visible = !!control && control.value === value;
      block.hidden = !visible;
      block.querySelectorAll('input, select, textarea').forEach((input) => {
        input.disabled = !visible;
      });
    });
  }

  function matchSignature() {
    const normalize = (value) => value.trim().replace(/\s+/g, ' ').toLowerCase();
    const matches = normalize(signature.value) === normalize(name.value);
    signature.setCustomValidity(!signature.value || matches ? '' : (text.signatureMismatch || '').replace('%s', name.value.trim()));
  }

  function syncAddButton(list) {
    const button = form.querySelector('[data-repeat-add="' + list.dataset.repeat + '"]');
    button.hidden = list.querySelectorAll('[data-repeat-item]').length >= Number(list.dataset.max);
  }

  form.addEventListener('click', (event) => {
    const add = event.target.closest('[data-repeat-add]');
    const remove = event.target.closest('[data-repeat-remove]');
    const jump = event.target.closest('[data-step-nav]');

    if (add) {
      const list = form.querySelector('[data-repeat="' + add.dataset.repeatAdd + '"]');
      const index = (Number(list.dataset.next) || 2);
      list.dataset.next = index + 1;
      const template = list.querySelector('template');
      template.insertAdjacentHTML('beforebegin', template.innerHTML.replace(/__i__/g, index));
      syncAddButton(list);
      template.previousElementSibling.querySelector('input').focus();
    } else if (remove) {
      const list = remove.closest('[data-repeat]');
      remove.closest('[data-repeat-item]').remove();
      syncAddButton(list);
    } else if (jump) {
      show(navButtons.indexOf(jump));
    } else if (event.target.closest('[data-step-prev]')) {
      show(current - 1);
    }
  });

  form.addEventListener('change', (event) => {
    const field = event.target;
    dirty = true;

    if (field.matches('[data-title-case]')) {
      field.value = field.value.replace(/\b[a-z]/g, (letter) => letter.toUpperCase());
    }
    if (field.type === 'file') {
      form.querySelector('[data-file-name]').textContent = field.files.length ? field.files[0].name : text.noFile;
    }
    if (field === name || field === signature) {
      signature.placeholder = name.value;
      matchSignature();
    }
    syncConditionals();
  });

  signature.addEventListener('input', matchSignature);

  async function post() {
    return fetch(ajaxUrl, { method: 'POST', body: new FormData(form), credentials: 'same-origin' });
  }

  async function refreshNonce() {
    const data = new FormData();
    data.append('action', 'pcm_fresh_form_nonces');
    data.append('fields[]', '_pcm_form_submit_nonce');
    const result = await (await fetch(ajaxUrl, { method: 'POST', body: data, credentials: 'same-origin' })).json();
    if (!result || !result.success || !result.data._pcm_form_submit_nonce) {
      throw new Error(text.refresh);
    }
    form.elements._pcm_form_submit_nonce.value = result.data._pcm_form_submit_nonce;
  }

  // A text element, with jobs@postcardmania.com turned into a mailto link.
  function line(message, tag, className) {
    const element = document.createElement(tag || 'p');
    if (className) {
      element.className = className;
    }
    String(message || '').split(/(jobs@postcardmania\.com)/).forEach((part, i) => {
      if (i % 2) {
        const link = document.createElement('a');
        link.href = 'mailto:' + part;
        link.textContent = part;
        element.append(link);
      } else {
        element.append(part);
      }
    });
    return element;
  }

  // Swap the form for where the application stands. When part of it didn't
  // reach the hiring system, the server says what went wrong and what, if
  // anything, the applicant still has to do - and the form goes away, since
  // submitting again would only repeat a failure we already know about.
  function finish(data) {
    dirty = false;
    const done = document.createElement('div');
    done.className = 'single-job-panel job-app__done';
    done.setAttribute('tabindex', '-1');
    if (data.outcome === 'stop') {
      done.innerHTML = text.notSent;
      done.append(line(data.message));
    } else {
      done.innerHTML = text.thanks;
      if (data.issues && data.issues.length) {
        const notice = document.createElement('div');
        notice.className = 'job-app__notice';
        const list = document.createElement('ul');
        data.issues.forEach((issue) => list.append(line(issue, 'li')));
        notice.append(line(data.intro), list, line(data.action, 'p', 'job-app__notice-action'), line(data.resubmit));
        done.lastElementChild.before(notice);
      }
    }
    form.replaceWith(done);
    done.scrollIntoView({ behavior: 'smooth', block: 'start' });
    done.focus({ preventScroll: true });
    try {
      sessionStorage.removeItem('pcm_job_lead_email');
    } catch (storageError) {
      // Nothing to clean up.
    }
  }

  async function submit() {
    sending = true;
    next.disabled = true;
    prev.disabled = true;
    nextLabel.textContent = text.sending;
    setStatus('');

    try {
      let response = await post();
      if (response.status === 403) {
        await refreshNonce();
        response = await post();
      }

      let result = null;
      try {
        result = await response.json();
      } catch (parseError) {
        // A PHP fatal or proxy error page: fall through to the generic message.
      }
      if (!result || !result.success) {
        const failure = new Error(result && result.data && result.data.message ? result.data.message : text.error);
        failure.data = result && result.data;
        throw failure;
      }
      finish(result.data || {});
    } catch (error) {
      if (error.data && error.data.outcome === 'stop') {
        // Submitting again can't work; the message says what to do instead.
        finish(error.data);
        return;
      }
      setStatus(error.message || text.error, 'error');
      next.disabled = false;
      prev.disabled = false;
      nextLabel.textContent = text.submit;
    } finally {
      sending = false;
    }
  }

  // Next is a plain button, not type="submit": accessibility.js disables and
  // relabels a form's submit button on every submit event, which would lock
  // the form after step 1. Enter in a single-line field advances instead.
  // The step list lets a visitor jump back and edit, so the last step
  // re-checks every step before sending.
  function advance() {
    if (sending) {
      return;
    }
    if (current < steps.length - 1) {
      if (stepIsValid(steps[current])) {
        show(current + 1);
      }
      return;
    }
    const firstInvalid = steps.findIndex((step) => !Array.from(step.querySelectorAll('input, select, textarea')).every((field) => field.checkValidity()));
    if (firstInvalid !== -1) {
      if (firstInvalid !== current) {
        show(firstInvalid);
      }
      stepIsValid(steps[firstInvalid]);
      return;
    }
    submit();
  }

  next.addEventListener('click', advance);
  form.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' && event.target.matches('input:not([type="checkbox"]):not([type="radio"]):not([type="file"])')) {
      event.preventDefault();
      advance();
    }
  });
  form.addEventListener('submit', (event) => event.preventDefault());

  window.addEventListener('beforeunload', (event) => {
    if (dirty && !sending) {
      event.preventDefault();
      event.returnValue = '';
    }
  });

  // The careers email capture stores the address it collected.
  if (!form.elements.email.value) {
    try {
      form.elements.email.value = sessionStorage.getItem('pcm_job_lead_email') || '';
    } catch (storageError) {
      // Storage disabled: the field just starts empty.
    }
  }

  syncConditionals();
})();
